import { useEffect, useState } from "react";
import { Alert, Button, ScrollArea, SegmentedControl } from "@one-nexty/components-web";

import { Section, SubSection } from "../ui/section";

/**
 * Les deux spécifications pour agents.
 *
 * Il y en a deux parce qu'il y a deux cibles, et qu'une spécification unique
 * mêlant les deux ferait écrire du code qui ne compile sur aucune : les APIs
 * sont proches, jamais identiques — `Select` lit ses enfants sur le web et prend
 * une prop `options` en natif, une icône hérite de `currentColor` ici et reçoit
 * une prop `color` là-bas.
 *
 * Celle du natif est GÉNÉRÉE depuis les sources TypeScript ; celle du web est
 * encore tenue à la main, et a dérivé.
 */
const SPECS = {
  web: {
    path: "/llms.txt",
    label: "Web",
    packages: "@one-nexty/components-web & @one-nexty/icons",
    summary:
      "Installation, configuration Tailwind v4, règles d'accessibilité, puis la signature et un exemple pour chaque composant.",
  },
  native: {
    path: "/llms-native.txt",
    label: "React Native",
    packages: "@one-nexty/components-native, @one-nexty/icons-native & @one-nexty/native-theme",
    summary:
      "Installation, mise en place des trois fournisseurs, principes propres au natif (le thème est un objet, le texte n'hérite de rien), puis la documentation et les signatures de chaque module — extraites des sources, donc exactes par construction. Se termine par le tableau des écarts avec la cible web.",
  },
} as const;

type SpecTarget = keyof typeof SPECS;

export function AiGuideSection() {
  const [target, setTarget] = useState<SpecTarget>("native");
  const [copied, setCopied] = useState(false);
  const [text, setText] = useState("");

  const spec = SPECS[target];

  useEffect(() => {
    let cancelled = false;
    setText("");

    fetch(spec.path)
      .then((res) => res.text())
      .then((body) => {
        if (!cancelled) setText(body);
      })
      .catch(() => {
        if (!cancelled) setText(`Impossible de charger ${spec.path}`);
      });

    // Changer de cible pendant qu'une requête est en vol afficherait sinon la
    // spécification de l'autre.
    return () => {
      cancelled = true;
    };
  }, [spec.path]);

  const handleCopy = () => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Section
      id="specification-ia"
      eyebrow="Guide"
      title="llms.txt"
      description="Signature TypeScript et usage de chaque composant, en texte brut, pour être consommés par un agent. Une spécification par cible : les deux APIs sont proches, jamais identiques."
    >
      <SubSection title="Cible" hint="Les routes servent du text/plain">
        <div className="flex flex-col gap-4">
          <SegmentedControl
            aria-label="Cible de la spécification"
            value={target}
            onValueChange={(value) => setTarget(value as SpecTarget)}
            className="max-w-xs"
            options={[
              { value: "native", label: SPECS.native.label },
              { value: "web", label: SPECS.web.label },
            ]}
          />

          <p className="text-sm text-fg-muted">
            <code className="font-mono text-xs">{spec.packages}</code>
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" onClick={() => window.open(spec.path, "_blank")}>
              Ouvrir {spec.path}
            </Button>

            <Button variant="outline" onClick={handleCopy} disabled={!text}>
              {copied ? "Copié" : "Copier le texte brut"}
            </Button>
          </div>
        </div>
      </SubSection>

      <SubSection title="Aperçu">
        <div className="mb-4">
          <Alert tone="info" title="Ce que contient le fichier">
            {spec.summary}
          </Alert>
        </div>

        <ScrollArea
          fade
          maxHeight="max-h-[500px]"
          className="rounded-lg border border-border bg-surface-sunken p-4"
        >
          <pre className="font-mono text-xs leading-relaxed whitespace-pre-wrap text-fg">
            {text || "Chargement…"}
          </pre>
        </ScrollArea>
      </SubSection>
    </Section>
  );
}
